/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../../../core/components';
import { Component } from '@angular/core';
import { ErrorResponseModel } from '../../../../../../../core/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
    LanguageService,
    LogService,
    RbacService,
    SocketService,
    UserService,
    UtilsService
} from '../../../../../../../core/services';
import { NamespaceService } from '../../../../../shared/services/utils';
import { Observable, Subscriber } from 'rxjs';
import { OrganisationsApiService, ShuttleApiService } from '../../../../../shared/http';
import { ShuttleModel, SuppliersModel } from '../../../../../shared/models';
import { ShuttleSuppliers } from '../../../../../shared/lists';
import { takeUntil } from 'rxjs/operators';

declare let Intercom: any;

@Component({
    selector: 'app-shuttle-add-details',
    templateUrl: './shuttle-add-details.component.html',
    styleUrls: ['./shuttle-add-details.component.scss']
})
export class ShuttleAddDetailsComponent extends BaseComponent {
    /**
     * Selected supplier id.
     */
    public selectedVendorId: number;

    /**
     * Selected supplier title for the form
     */
    public selectedSupplier: SuppliersModel;

    /**
     * Store the current step.
     */
    public activeStep: number = 1;

    /**
     * New Loadbalancer FormGroup
     */
    public formGroup: FormGroup;

    /**
     * Store the shuttle dropdown content for use in the view.
     */
    public dropdown: any = {
        connectionMethod: []
    };

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * Flag to store the shuttle attached state.
     */
    public shuttleAttached: boolean = false;

    /**
     * Store the org token for shuttles.
     */
    public token: string = null;

    /**
     * Store WS response for add shuttle
     */
    public newlyAddedShuttles: ShuttleModel[] = [];

    /**
     * Namespace selected key.
     * @private
     */
    private selectedNamespaceKey: number;

    constructor(
        private activatedRoute: ActivatedRoute,
        private formBuilder: FormBuilder,
        private languageService: LanguageService,
        private logService: LogService,
        private namespaceService: NamespaceService,
        private organisationApiService: OrganisationsApiService,
        private rbacService: RbacService,
        private router: Router,
        private shuttleApiService: ShuttleApiService,
        private socketService: SocketService,
        private userService: UserService,
        private utilsService: UtilsService
    ) {
        super();

        this.selectedNamespaceKey = this.namespaceService.selectedNamespace.key;
        this.setupSubscribers();
        this.setUpForm();
        this.getOrganisationDetails();
        this.setUpPageByVendor();
        this.setupSocketListener();
        this.registerButtons();
    }

    /**
     * Go back a step in the process.
     */
    public backStep(): void {
        this.activeStep--;
    }

    /**
     * Go forward a step in the process.
     */
    public nextStep(): void {
        this.activeStep++;
    }

    /**
     * Copy and skip to next step.
     */
    public copyNextStep(): void {
        const timerId = setTimeout(() => {
            this.nextStep();
            clearTimeout(timerId);
        }, 500);
    }

    /**
     * Go to a specific step in the process.
     * @param number
     */
    public setStep(number: number): void {
        this.activeStep = number;
    }

    /**
     * Returns the form group as FormGroup.
     * @param group
     */
    public getFormGroup(group: any): FormGroup {
        return this.formGroup.get(group) as FormGroup;
    }

    /**
     * Set back button disabled state.
     */
    public isBackButtonDisabled(): boolean {
        return this.activeStep === 1;
    }

    /**
     * Check if the button is disabled based on the form step state.
     */
    public isNextButtonDisabled(): boolean {
        return this.formGroup.get('step' + this.activeStep).invalid;
    }

    /**
     * Adopt Shuttle.
     * @param shuttle
     */
    public adoptShuttle(shuttle: ShuttleModel): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const timerId: any = setTimeout((): void => { // UX improvement API very fast.
                this.shuttleApiService.adopt(this.selectedNamespaceKey, shuttle.id)
                    .pipe(takeUntil(this.destroyed$))
                    .subscribe({
                        next: (): void => {
                            const deleteIndex = this.newlyAddedShuttles.findIndex(item => item.id === shuttle.id);
                            if (deleteIndex !== -1) {
                                this.newlyAddedShuttles.splice(deleteIndex, 1);
                            }
                            if (!this.newlyAddedShuttles.length) {
                                this.router.navigateByUrl('/cm/adcs/shuttle').then().catch();
                            }
                            observer.next();
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            this.logService.error(
                                'ShuttleAddDetailsComponent:adoptShuttle',
                                'Unable to adopt shuttle' + error.message
                            );
                            observer.error(error);
                            observer.complete();
                        }
                    });

                clearTimeout(timerId);
            }, 1000);
        });
    }

    /**
     * CTA to open intercom chat.
     */
    public contactSupport(): void {
        const message = `${this.languageService.translate('adc_shuttle_intercom_message')}`;
        Intercom('showNewMessage', message);
    }

    /**
     * Setup page subscribers.
     * @private
     */
    private setupSubscribers(): void {
        this.namespaceService.selectedEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (namespace: any): void => {
                    this.selectedNamespaceKey = namespace.key;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('AdcsListComponent: setupSubscribers', 'Unable to get selected namespace.', error);
                }
            });
    }

    /**
     * Set up the add load balancer form.
     * @private
     */
    private setUpForm(): void {
        this.formGroup = this.formBuilder.group({
            step1: this.formBuilder.group({
                connectionMethod: [null, Validators.required]
            }),
            step2: this.formBuilder.group({
                null: [null]
            }),
            step3: this.formBuilder.group({
                null: [null]
            }),
        });
    }

    /**
     * Set up the page by vendor.
     * @private
     */
    private setUpPageByVendor(): void {
        const vendorId: string = this.activatedRoute.snapshot.paramMap.get('vendorId');
        const selectedSupplier = ShuttleSuppliers.find(item => item.vendorId === parseInt(vendorId, 10));
        if (selectedSupplier) {
            this.selectedVendorId = selectedSupplier.vendorId;
            this.selectedSupplier = selectedSupplier;
            this.setUpDropdown();
        } else {
            this.logService.error('setUpPageByVendor', 'Unable to match a vendor in the vendors list');
        }
    }

    /**
     * Get token from organisation details.
     * @private
     */
    private getOrganisationDetails(): void {
        const organisationId = this.utilsService.duplicateObject(this.userService.getUser('organisationId'));
        this.organisationApiService.get(organisationId.toString())
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (organisationDetails: any) => {
                    if (this.selectedVendorId !== 7) {
                        this.token = 'lbsidecar --adopt=' + organisationDetails.adoptionToken;
                    } else {
                        if (this.environment.production) {
                            // eslint-disable-next-line max-len
                            this.token = 'sudo bash -c "$(curl -fsSL http://lbshuttle.sh/install.sh)" && lbsidecar --adopt=' + organisationDetails.adoptionToken;

                        } else {
                            // eslint-disable-next-line max-len
                            this.token = 'sudo bash -c "$(curl -fsSL http://dev.lbshuttle.sh/install.sh)" && lbsidecar --adopt=' + organisationDetails.adoptionToken;
                        }
                    }
                }
            });
    }

    /**
     * Setup dropdown options.
     * @private
     */
    private setUpDropdown(): void {
        const dropdowns = [];
        if (this.selectedVendorId !== 7) {
            dropdowns.push({
                key: 'credentials',
                value: 'Credentials'
            });
        }

        dropdowns.push({
                key: 'token',
                value: 'Token'
            }
        );

        this.dropdown.connectionMethod = dropdowns;
    }

    /**
     * Setup socket listeners.
     * @private
     */
    private setupSocketListener(): void {
        this.logService.debug('ShuttleAddDetailsComponent:SetupSocketListener', 'Started listening for \'sidecarAdd\'.');
        this.socketService.on('sidecarAdd')
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (shuttle: any): void => {
                    this.newlyAddedShuttles.push(shuttle.data);
                    this.shuttleAttached = true;
                    this.nextStep();
                }
            });
    }

    /**
     * Register the button permissions for the page.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'adcs-shuttle-adopt': [
                { path: '/api/v1/:namespace/sidecars/:id/adopt', methods: ['PATCH'] },
            ]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }
}
